import React, { useState } from 'react';
import Header from './Header';
import Footer from './Footer';
import { searchHotels } from './services/hotelService';
import './HotelSearchPage.css';

const HotelSearchPage = () => {
  const [searchParams, setSearchParams] = useState({
    cityCode: 'PAR', // Paris as default
    checkInDate: '',
    checkOutDate: '',
    adults: 1,
    roomQuantity: 1,
    currency: 'NGN'
  });

  const [loading, setLoading] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [error, setError] = useState(null);
  const [searchExecuted, setSearchExecuted] = useState(false);

  // Set default dates
  React.useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const checkIn = tomorrow.toISOString().split('T')[0];

    const dayAfter = new Date(tomorrow);
    dayAfter.setDate(dayAfter.getDate() + 1);
    const checkOut = dayAfter.toISOString().split('T')[0];

    setSearchParams(prev => ({
      ...prev,
      checkInDate: checkIn,
      checkOutDate: checkOut
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setHotels([]);
    setSearchExecuted(false);

    try {
      const result = await searchHotels(searchParams);
      
      if (result.success) {
        setHotels(result.data);
        setSearchExecuted(true);
      } else {
        setError(result.error || 'Failed to search hotels');
        console.error('Search error:', result);
      }
    } catch (err) {
      setError(err.message || 'An unexpected error occurred');
      console.error('Search exception:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price || !price.total) return 'Price not available';
    const amount = parseFloat(price.total);
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: price.currency || 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const calculateNights = () => {
    if (!searchParams.checkInDate || !searchParams.checkOutDate) return 0;
    const checkIn = new Date(searchParams.checkInDate);
    const checkOut = new Date(searchParams.checkOutDate);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Popular city codes
  const popularCities = [
    { code: 'PAR', name: 'Paris, France' },
    { code: 'NYC', name: 'New York, USA' },
    { code: 'LON', name: 'London, UK' },
    { code: 'DXB', name: 'Dubai, UAE' },
    { code: 'SYD', name: 'Sydney, Australia' },
    { code: 'TOK', name: 'Tokyo, Japan' },
    { code: 'ROM', name: 'Rome, Italy' },
    { code: 'BCN', name: 'Barcelona, Spain' }
  ];

  return (
    <div className="hotel-search-page">
      <Header />
      
      <div className="hotel-page-container">
        <div className="hotel-page-header">
          <h1>Hotel Search</h1>
          <p>Find and book hotels worldwide with real-time availability</p>
        </div>

        {/* Search Form */}
        <div className="hotel-search-form">
          <form onSubmit={handleSearch}>
            <div className="form-row">
              <div className="form-group">
                <label>City Code</label>
                <input
                  type="text"
                  name="cityCode"
                  value={searchParams.cityCode}
                  onChange={handleInputChange}
                  placeholder="e.g., PAR, NYC, LON"
                  required
                  maxLength="3"
                  style={{ textTransform: 'uppercase' }}
                />
                <small>Use 3-letter city codes (IATA)</small>
              </div>

              <div className="form-group">
                <label>Check-In Date</label>
                <input
                  type="date"
                  name="checkInDate"
                  value={searchParams.checkInDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="form-group">
                <label>Check-Out Date</label>
                <input
                  type="date"
                  name="checkOutDate"
                  value={searchParams.checkOutDate}
                  onChange={handleInputChange}
                  required
                  min={searchParams.checkInDate || new Date().toISOString().split('T')[0]}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Adults</label>
                <input
                  type="number"
                  name="adults"
                  value={searchParams.adults}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                />
              </div>

              <div className="form-group">
                <label>Rooms</label>
                <input
                  type="number"
                  name="roomQuantity"
                  value={searchParams.roomQuantity}
                  onChange={handleInputChange}
                  min="1"
                  max="10"
                />
              </div>

              <div className="form-group">
                <label>Currency</label>
                <select
                  name="currency"
                  value={searchParams.currency}
                  onChange={handleInputChange}
                >
                  <option value="NGN">NGN (Naira)</option>
                  <option value="USD">USD (Dollar)</option>
                  <option value="EUR">EUR (Euro)</option>
                  <option value="GBP">GBP (Pound)</option>
                </select>
              </div>
            </div>

            <div className="popular-cities">
              <label>Quick Select:</label>
              <div className="city-buttons">
                {popularCities.map(city => (
                  <button
                    key={city.code}
                    type="button"
                    className="city-button"
                    onClick={() => setSearchParams(prev => ({ ...prev, cityCode: city.code }))}
                  >
                    {city.name}
                  </button>
                ))}
              </div>
            </div>

            <button 
              type="submit" 
              className="search-button"
              disabled={loading}
            >
              {loading ? 'Searching Hotels...' : 'Search Hotels'}
            </button>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Searching for hotels...</p>
          </div>
        )}

        {/* Results */}
        {searchExecuted && !loading && (
          <div className="results-section">
            <h2>
              {hotels.length > 0 
                ? `Found ${hotels.length} Hotel${hotels.length !== 1 ? 's' : ''} in ${searchParams.cityCode}`
                : 'No Hotels Found'
              }
            </h2>

            {searchParams.checkInDate && searchParams.checkOutDate && (
              <div className="search-summary">
                <span>{formatDate(searchParams.checkInDate)}</span>
                <span>→</span>
                <span>{formatDate(searchParams.checkOutDate)}</span>
                <span>({calculateNights()} night{calculateNights() !== 1 ? 's' : ''})</span>
              </div>
            )}

            {hotels.length > 0 ? (
              <div className="hotels-list">
                {hotels.map((hotel, index) => (
                  <div key={hotel.hotelId || index} className="hotel-card">
                    <div className="hotel-header">
                      <div className="hotel-info">
                        <h3 className="hotel-name">{hotel.name}</h3>
                        {hotel.rating && (
                          <div className="hotel-rating">
                            {'⭐'.repeat(Math.floor(hotel.rating))}
                            <span className="rating-text">{hotel.rating}/5</span>
                          </div>
                        )}
                      </div>
                      {hotel.price && (
                        <div className="hotel-price">
                          <div className="price-amount">{formatPrice(hotel.price)}</div>
                          <div className="price-period">per night</div>
                        </div>
                      )}
                    </div>

                    {hotel.address && (
                      <div className="hotel-address">
                        <span className="address-icon">📍</span>
                        <span>
                          {hotel.address.lines?.join(', ') || ''}
                          {hotel.address.cityName && `, ${hotel.address.cityName}`}
                          {hotel.address.countryCode && `, ${hotel.address.countryCode}`}
                        </span>
                      </div>
                    )}

                    {hotel.hotelDistance && (
                      <div className="hotel-distance">
                        <span>Distance: {hotel.hotelDistance.distance} {hotel.hotelDistance.distanceUnit}</span>
                        {hotel.hotelDistance.direction && <span> ({hotel.hotelDistance.direction})</span>}
                      </div>
                    )}

                    {hotel.amenities && hotel.amenities.length > 0 && (
                      <div className="hotel-amenities">
                        <strong>Amenities:</strong>
                        <div className="amenities-list">
                          {hotel.amenities.map((amenity, idx) => (
                            <span key={idx} className="amenity-badge">{amenity}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {hotel.description && (
                      <div className="hotel-description">
                        <p>{hotel.description}</p>
                      </div>
                    )}

                    {hotel.offers && hotel.offers.length > 0 && (
                      <div className="hotel-offers">
                        <strong>Available Offers:</strong>
                        {hotel.offers.slice(0, 3).map((offer, idx) => (
                          <div key={idx} className="offer-item">
                            <span>{offer.room?.type || 'Room'}</span>
                            {offer.price && (
                              <span className="offer-price">{formatPrice(offer.price)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="hotel-raw-data">
                      <details>
                        <summary>View Raw API Data</summary>
                        <pre>{JSON.stringify(hotel, null, 2)}</pre>
                      </details>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-hotels">
                <p>No hotels found for your search criteria. Try a different city code or dates.</p>
                <p><strong>Tip:</strong> Use 3-letter IATA city codes like PAR, NYC, LON, DXB</p>
              </div>
            )}
          </div>
        )}

        {/* Info Section */}
        <div className="info-section">
          <h3>Hotel Search Information</h3>
          <ul>
            <li><strong>API:</strong> Amadeus Hotel Search API</li>
            <li><strong>Status:</strong> {searchExecuted ? (hotels.length > 0 ? '✅ Working' : '⚠️ No Results') : 'Ready to test'}</li>
            <li><strong>City Codes:</strong> Use 3-letter IATA city codes (e.g., PAR for Paris, NYC for New York)</li>
            <li><strong>Note:</strong> Some cities may have limited hotel availability in the test environment</li>
          </ul>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default HotelSearchPage;
